import { normalizePromotionPaymentCondition } from "../supabase/edge-functions/_shared/promotionPayments";
import { repairTextEncoding } from "./textEncoding";

const STORAGE_PREFIX = "mozzoapp:promotions:";
export const PROMOTIONS_UPDATED_EVENT = "mozzoapp:promotions:updated";
const SESSION_STORAGE_PREFIX = "mozzoapp:promotions:session:";
const promotionsMemoryCache = new Map();
const MAX_PROMOTION_DATA_IMAGE_LENGTH = 420000;

const toRenderableText = (value, fallback = "") => {
  if (typeof value === "string") {
    return value.trim();
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? String(value) : fallback;
  }

  if (typeof value === "boolean") {
    return value ? "Si" : "No";
  }

  return fallback;
};

const isHeavyPromotionImage = (value) =>
  typeof value === "string" &&
  value.startsWith("data:") &&
  value.length > MAX_PROMOTION_DATA_IMAGE_LENGTH;

const toDisplayText = (value, fallback = "") => repairTextEncoding(toRenderableText(value, fallback));

const normalizePromotionItem = (item) => {
  if (!item || typeof item !== "object") {
    return null;
  }

  return {
    ...item,
    productId: toRenderableText(item.productId || item.product_id),
    name: toDisplayText(item.name, "Producto"),
    quantity: Math.max(1, Number(item.quantity || 1)),
  };
};

export const normalizePromotionRecord = (record) => {
  if (!record || typeof record !== "object") {
    return null;
  }

  return {
    ...record,
    id: toRenderableText(record.id),
    title: toDisplayText(record.title, "Promocion"),
    type: record.type === "combo" ? "combo" : "promotion",
    comboKind: toRenderableText(record.comboKind),
    dailyMenuDate: toRenderableText(record.dailyMenuDate),
    hook: toDisplayText(record.hook),
    detail: toDisplayText(record.detail),
    pricingSummary: toDisplayText(record.pricingSummary),
    scheduleSummary: toDisplayText(record.scheduleSummary),
    validity: toDisplayText(record.validity),
    fontFamily: toRenderableText(record.fontFamily),
    customFontFamily: toRenderableText(record.customFontFamily),
    categoryName: toDisplayText(record.categoryName),
    subcategoryName: toDisplayText(record.subcategoryName),
    productName: toDisplayText(record.productName),
    targetLabel: toDisplayText(record.targetLabel),
    imageUrl: typeof record.imageUrl === "string" ? record.imageUrl.trim() : "",
    deliveryAvailable: record.deliveryAvailable === true,
    paymentCondition: normalizePromotionPaymentCondition(record.paymentCondition),
    schedule: Array.isArray(record.schedule) ? record.schedule : [],
    items: Array.isArray(record.items)
      ? record.items.map(normalizePromotionItem).filter(Boolean)
      : [],
  };
};

export const normalizePromotionsPayload = (promotions) =>
  (Array.isArray(promotions) ? promotions : [])
    .map(normalizePromotionRecord)
    .filter(Boolean);

export const reconcilePromotionImagesById = (sourceRecords, targetRecords) => {
  const safeSource = Array.isArray(sourceRecords) ? sourceRecords : [];
  const safeTarget = Array.isArray(targetRecords) ? targetRecords : [];
  let recovered = 0;

  const records = safeTarget.map((record) => {
    const sourceRecord = safeSource.find((item) => item?.id === record?.id);
    const sourceImageUrl =
      typeof sourceRecord?.imageUrl === "string" ? sourceRecord.imageUrl.trim() : "";
    const targetImageUrl =
      typeof record?.imageUrl === "string" ? record.imageUrl.trim() : "";

    if (!targetImageUrl && sourceImageUrl) {
      recovered += 1;
      return {
        ...record,
        imageUrl: sourceImageUrl,
      };
    }

    return record;
  });

  return {
    records,
    recovered,
  };
};

const sanitizePromotionForStorage = (record) => {
  if (!record || typeof record !== "object") {
    return record;
  }

  return {
    ...record,
    imageUrl: isHeavyPromotionImage(record.imageUrl) ? "" : record.imageUrl || "",
  };
};

const normalizeStoredPromotionsPayload = (promotions, options = {}) => {
  const payload = normalizePromotionsPayload(promotions);

  if (!options.compact) {
    return payload;
  }

  return payload.map((record) => sanitizePromotionForStorage(record));
};

export const buildPromotionsStorageKey = (restaurantId) =>
  `${STORAGE_PREFIX}${restaurantId || "default"}`;

const buildPromotionsSessionStorageKey = (restaurantId) =>
  `${SESSION_STORAGE_PREFIX}${restaurantId || "default"}`;

export const getPromotions = (restaurant) => {
  const restaurantId = restaurant?.id;
  const memoryValue = promotionsMemoryCache.get(restaurantId || "default");

  try {
    const storageKey = buildPromotionsStorageKey(restaurantId);
    const rawValue = window.localStorage.getItem(storageKey);

    if (!rawValue) {
      const sessionValue = window.sessionStorage.getItem(
        buildPromotionsSessionStorageKey(restaurantId)
      );

      if (!sessionValue) {
        return Array.isArray(memoryValue) ? memoryValue : [];
      }

      const parsedSessionValue = JSON.parse(sessionValue);
      return Array.isArray(parsedSessionValue)
        ? normalizePromotionsPayload(parsedSessionValue)
        : Array.isArray(memoryValue)
          ? normalizePromotionsPayload(memoryValue)
          : [];
    }

    const parsedValue = JSON.parse(rawValue);
    return Array.isArray(parsedValue)
      ? normalizePromotionsPayload(parsedValue)
      : Array.isArray(memoryValue)
        ? normalizePromotionsPayload(memoryValue)
        : [];
  } catch (error) {
    console.error("No pudimos leer las promociones de la sucursal:", error);
    return Array.isArray(memoryValue) ? normalizePromotionsPayload(memoryValue) : [];
  }
};

export const savePromotions = (restaurant, promotions, options = {}) => {
  const payload = normalizePromotionsPayload(promotions);
  const storedPayload = normalizeStoredPromotionsPayload(payload, options);
  const restaurantId = restaurant?.id;
  const memoryKey = restaurantId || "default";

  promotionsMemoryCache.set(memoryKey, payload);

  try {
    const storageKey = buildPromotionsStorageKey(restaurantId);
    window.localStorage.setItem(storageKey, JSON.stringify(storedPayload));
    try {
      window.sessionStorage.removeItem(buildPromotionsSessionStorageKey(restaurantId));
    } catch (error) {
      // Si no se puede limpiar la cache de sesion, seguimos igual.
    }
    window.dispatchEvent(
      new CustomEvent(PROMOTIONS_UPDATED_EVENT, {
        detail: {
          restaurantId,
          promotions: payload,
          compact: Boolean(options.compact),
          storageMode: "local",
        },
      })
    );
  } catch (error) {
    try {
      window.sessionStorage.setItem(
        buildPromotionsSessionStorageKey(restaurantId),
        JSON.stringify(storedPayload)
      );
      window.dispatchEvent(
        new CustomEvent(PROMOTIONS_UPDATED_EVENT, {
          detail: {
            restaurantId,
            promotions: payload,
            compact: Boolean(options.compact),
            storageMode: "session",
          },
        })
      );
      return payload;
    } catch (sessionError) {
      console.warn(
        "No pudimos guardar las promociones de la sucursal en storage persistente. Seguimos con cache en memoria de esta sesion.",
        sessionError
      );
      window.dispatchEvent(
        new CustomEvent(PROMOTIONS_UPDATED_EVENT, {
          detail: {
            restaurantId,
            promotions: payload,
            compact: Boolean(options.compact),
            storageMode: "memory",
          },
        })
      );
      return payload;
    }
  }

  return payload;
};
